import { MobileModal } from '@components/MobileModal/MobileModal';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { FileWithDuration } from '@lib/hooks/useFileMetadataExtraction';
import { useTopUp } from '@lib/hooks/useTopUp';
import { type IngestEstimate, summarizeDuration } from '@lib/utils/ingestEstimate';
import { OwnerAffordability, ownerShortfallBlock } from '@lib/utils/ownerAffordability';
import {
  formatAsEstimatedMaximum,
  formatAsEstimatedMinimum,
  formatBytesToString,
  formatCreditsWithUnit,
  pluralize,
} from '@lib/utils/units';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { DEFAULT_LIBRARY_NAME } from '@pages/Libraries/components/LibraryCreateModal';
import {
  BaseModal,
  BaseModalRef,
} from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { billingStore } from '@stores/Billing';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { useEffect, useRef } from 'react';
import { BilledToOwnerNotice } from './BilledToOwnerNotice';
import css from './ChooseFileUploadModalSummaryConfirm.module.scss';
import { CONTENT_RIGHTS_NOTICE } from './contentRightsNotice';
import { CreditUsageSummary } from './CreditUsageSummary';
import { MediaSourcePreview } from './MediaSourcePreview';

interface ChooseFileUploadModalSummaryConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (libraryName?: string, createNewLibrary?: boolean) => Promise<void>;
  selectedFiles: FileWithDuration[];
  isLoading?: boolean;
  createNewLibrary?: boolean;
  /** Name of the new library, chosen on the upload screen. Shown read-only here. */
  newLibraryName?: string;
  creditCost: number;
  storageAfterProcessing: number;
  ingestEstimate: IngestEstimate;
  /**
   * True when the target library is billed to another account. The credit summary below is
   * computed from `billingStore` — this user's balance — so it describes the wrong wallet and is
   * replaced by the owner notice; the top-up CTA and the balance gate on Upload go with it.
   */
  billedToAnotherAccount: boolean;
  /** The owner's display name, when the backend gave one. Only read while the flag above is set. */
  ownerName?: string;
  /** Replaces the balance gate the flag above removes. See `ownerAffordability.ts`. */
  ownerAffordability?: OwnerAffordability;
}

export const ChooseFileUploadModalSummaryConfirm = ({
  open,
  onClose,
  onConfirm,
  selectedFiles,
  isLoading = false,
  createNewLibrary = false,
  newLibraryName,
  creditCost,
  storageAfterProcessing,
  ingestEstimate,
  billedToAnotherAccount,
  ownerName,
  ownerAffordability,
}: ChooseFileUploadModalSummaryConfirmProps) => {
  const isMobile = useIsMobile();
  const baseModalRef = useRef<BaseModalRef>(null);

  useModalRegistration('choose-file-upload-summary', open);
  const currentCredits = billingStore.creditsLeft || 0;

  // Control modal open/close state
  useEffect(() => {
    if (open) {
      baseModalRef.current?.open();
    } else {
      baseModalRef.current?.close();
    }
  }, [open]);

  const calculateTotalSize = () => {
    return selectedFiles.reduce((sum, file) => sum + file.size, 0);
  };

  const calculateHasUnknownSize = () => {
    return selectedFiles.some((file) => file.size === undefined || file.size === 0);
  };

  const totalSize = calculateTotalSize();
  const { durationSeconds: totalDuration, hasUnknownDuration, imageCount } = ingestEstimate;
  const duration = summarizeDuration(ingestEstimate);
  const hasUnknownSize = calculateHasUnknownSize();
  const creditsAfterUpload = currentCredits - creditCost;
  // See ChooseFileUploadModal: when the owner pays, this account has nothing to top up, and
  // `needsAction` staying false is also what keeps the Upload button enabled below.
  const topUp = useTopUp({
    creditCost,
    currentCredits,
    insufficient: !billedToAnotherAccount && creditsAfterUpload <= 0,
    source: 'file-upload-summary',
    ...(billedToAnotherAccount
      ? {}
      : {
          storage: {
            afterProcessing: storageAfterProcessing,
            duration: ingestEstimate.durationSeconds,
          },
        }),
  });

  // The owner-billed counterpart to `topUp.needsAction`. Shared with `getUploadDisabledState`,
  // which the URL flow's confirm screen goes through, so the two cannot drift.
  const ownerBlock = ownerShortfallBlock(billedToAnotherAccount, ownerAffordability, ownerName);
  const ownerShortfall = ownerBlock?.shortfall;
  const ownerShortfallReason = ownerBlock?.reason ?? null;

  const handleConfirm = async () => {
    trackingStore.fileUploadSummaryConfirmed({
      fileCount: selectedFiles.length,
      totalDurationSeconds: totalDuration,
      totalSizeBytes: totalSize,
      creditsCost: creditCost,
      creditsAvailable: currentCredits,
    });
    // Library name now comes from the upload screen (injected by the flow); the
    // confirm step no longer edits it.
    await onConfirm(undefined, createNewLibrary);
  };

  const handleModalClose = () => {
    // Guard: only track when the modal was actually visible. Without this,
    // the useEffect that syncs the `open` prop fires close() on initial mount
    // (open=false), which triggers this callback spuriously.
    if (open) {
      trackingStore.fileUploadSummaryCancelled({ fileCount: selectedFiles.length });
    }
    onClose();
  };

  const summaryContent = (
    <>
      {createNewLibrary && (
        <div className={css.formFieldAndLabel}>
          <label className={css.label}>New library</label>
          <div className={css.readOnlyValue}>{newLibraryName?.trim() || DEFAULT_LIBRARY_NAME}</div>
        </div>
      )}

      {selectedFiles.length === 1 && selectedFiles[0] && (
        <div className={css.formFieldAndLabel}>
          <label>File</label>
          <MediaSourcePreview
            label="File"
            image={selectedFiles[0].thumbnail}
            title={selectedFiles[0].name}
            subtitle={formatBytesToString(selectedFiles[0].size, { precision: 2, detail: 1 })}
          />
        </div>
      )}
      {selectedFiles.length > 1 && (
        <div className={css.formFieldAndLabel}>
          <label>Files</label>
          <MediaSourcePreview
            label="Files"
            image={selectedFiles[0]?.thumbnail}
            title={`${selectedFiles.length} files selected`}
            subtitle={formatBytesToString(totalSize, { precision: 2, detail: 1 })}
          />
        </div>
      )}

      <div className={css.formFieldAndLabel}>
        <label htmlFor="summary-upload">Upload summary</label>
        <div className={css.summaryUploadContainer}>
          <div className={css.summaryBlock}>
            <div className={css.summaryBlockNumber}>{selectedFiles.length}</div>
            <div className={css.summaryBlockText}>files</div>
          </div>
          {imageCount > 0 && (
            <div className={css.summaryBlock}>
              <div className={css.summaryBlockNumber}>{imageCount}</div>
              <div className={css.summaryBlockText}>{imageCount === 1 ? 'image' : 'images'}</div>
            </div>
          )}
          {duration && (
            <div className={css.summaryBlock}>
              <div className={css.summaryBlockNumber}>
                {duration.kind === 'minimum' && <span className={css.minimumPrefix}>≥&nbsp;</span>}
                {duration.text}
                {duration.kind !== 'exact' && (
                  <Tooltip title="Some file types don't allow us to estimate file duration before upload.">
                    <InfoOutlined className={css.infoIcon} />
                  </Tooltip>
                )}
              </div>
              <div className={css.summaryBlockText}>total duration</div>
            </div>
          )}
          <div className={css.summaryBlock}>
            <div className={css.summaryBlockNumber}>
              {hasUnknownSize && <span className={css.minimumPrefix}>≥</span>}
              {formatBytesToString(totalSize, { precision: 2, detail: 1 })}
              {hasUnknownSize && (
                <Tooltip title="Some file types don't allow us to estimate file size before upload.">
                  <InfoOutlined className={css.infoIcon} />
                </Tooltip>
              )}
            </div>
            <div className={css.summaryBlockText}>total size</div>
          </div>
        </div>
      </div>

      <div className={css.formFieldAndLabel}>
        <label htmlFor="summary-upload">Credit usage</label>
        {/* The cost estimate is a property of the media, so it stands whoever pays for it.
            "Remaining after" is not: it subtracts from this user's balance, which isn't the
            balance being charged when the library's owner is billed — so that row drops out
            and the notice explains why. */}
        <CreditUsageSummary
          cost={{
            label: 'Provisional cost for adding these media',
            tooltip: "Some file types don't allow us to estimate cost before upload.",
            items: [
              {
                text: formatAsEstimatedMinimum({
                  formatted: formatCreditsWithUnit(creditCost, 'up'),
                  uncertain: hasUnknownDuration || hasUnknownSize,
                  approximate: true,
                }),
              },
            ],
          }}
          remaining={
            billedToAnotherAccount
              ? undefined
              : {
                  label: 'Provisional remaining after adding these media',
                  items: [
                    {
                      text: formatAsEstimatedMaximum({
                        formatted: formatCreditsWithUnit(creditsAfterUpload, 'down'),
                        uncertain: hasUnknownDuration || hasUnknownSize,
                        approximate: true,
                      }),
                      variant: creditsAfterUpload < 0 ? 'red' : 'green',
                    },
                  ],
                }
          }
        />
        {billedToAnotherAccount && (
          <BilledToOwnerNotice
            ownerName={ownerName}
            shortfall={ownerShortfall}
            className={css.billedToOwnerNotice}
          />
        )}
      </div>
    </>
  );

  const actionButtons = (
    <>
      {!(isMobile && topUp.needsAction && !isLoading) && (
        <ActionButton
          variant="secondary"
          onClick={handleModalClose}
          disabled={isLoading}
          padding={32}
        >
          Cancel
        </ActionButton>
      )}
      {topUp.needsAction && topUp.buttonLabel && !isLoading && (
        <Tooltip title={topUp.tooltip ?? ''} placement="top" arrow>
          <span>
            <ActionButton onClick={topUp.handleTopUp} disabled={topUp.isToppingUp} padding={32}>
              {topUp.buttonLabel}
            </ActionButton>
          </span>
        </Tooltip>
      )}
      <Tooltip
        title={
          ownerShortfallReason
            ? ownerShortfallReason
            : topUp.needsAction
              ? topUp.isStoragePriority
                ? 'Not enough storage.'
                : 'Not enough credits.'
              : isLoading
                ? 'Uploading your files...'
                : ''
        }
        placement="top"
        arrow
        disableHoverListener={!isLoading && !topUp.needsAction && !ownerShortfallReason}
        disableFocusListener={!isLoading && !topUp.needsAction && !ownerShortfallReason}
        disableTouchListener={!isLoading && !topUp.needsAction && !ownerShortfallReason}
      >
        <span>
          <ActionButton
            onClick={handleConfirm}
            disabled={isLoading || topUp.needsAction || !!ownerShortfallReason}
            variant={topUp.needsAction && !isLoading ? 'secondary' : undefined}
            padding={32}
          >
            {isLoading ? 'Uploading...' : `Upload ${pluralize(selectedFiles.length, 'file')}`}
          </ActionButton>
        </span>
      </Tooltip>
    </>
  );

  const rightsNotice = <p className={css.rightsNotice}>{CONTENT_RIGHTS_NOTICE}</p>;

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={handleModalClose}
        title="Confirm file upload"
        subtitle="Review the files you are about to add to your library."
        footer={
          <div className={css.mobileFooter}>
            {rightsNotice}
            <div className={css.mobileFooterButtons}>{actionButtons}</div>
          </div>
        }
      >
        <div className={css.mobileContent}>{summaryContent}</div>
      </MobileModal>
    );
  }

  return (
    <BaseModal
      ref={baseModalRef}
      onClose={handleModalClose}
      title="Confirm file upload"
      subtitle="Review the files you are about to add to your library."
      className={css.modal}
    >
      {summaryContent}
      {rightsNotice}
      <div className={css.actionButtons}>{actionButtons}</div>
    </BaseModal>
  );
};
